import { CardComponentProps, Variant } from "@~/types/types";
import styles from "./ColourSwatches.module.scss";
import classNames from "classnames/bind";
import { useVariant } from "@~/contexts/VariantContext/VariantContext";
import { useFeatureFlags } from "@~/flags/flags";
import { useEffect, useRef, useState } from "react";
import { dispatchAdTechAnalyticsEvent } from "@~/utils/analytics";

const cx = classNames.bind(styles);

function getUniqueColourVariantsWithSwatches(variants: Variant[]): Variant[] {
  return variants.filter((variant, index, arr) => {
    return (
      "colourSwatch" in variant &&
      arr.findIndex(
        (v: Variant) =>
          v.differentiators?.colour === variant.differentiators?.colour,
      ) === index
    );
  });
}

const getMaxSwatchesToDisplay = (swatchListWidth: number) => {
  if (Math.round(swatchListWidth) <= 120) {
    return 3;
  }
  if (Math.round(swatchListWidth) <= 175) {
    return 5;
  }
  if (Math.round(swatchListWidth) <= 260) {
    return 6;
  }
  return 10;
};

export function ColourSwatches({
  product,
}: Pick<CardComponentProps, "product">) {
  const { variants } = product;
  const { variant: selectedVariant, setSelectedColour } = useVariant();

  const colourSwatchVariants = getUniqueColourVariantsWithSwatches(variants);

  const [totals, setTotals] = useState({
    displayed: colourSwatchVariants?.length,
    notDisplayed: 0,
  });

  const swatchListRef = useRef<HTMLDivElement>(null);
  const { isShowNumberOfSwatchesEnabled } = useFeatureFlags();

  const showNumberOfSwatches = isShowNumberOfSwatchesEnabled.value;

  useEffect(() => {
    if (showNumberOfSwatches) {
      if (!swatchListRef.current) {
        return;
      }
      const displayed = getMaxSwatchesToDisplay(
        swatchListRef.current.clientWidth,
      );
      const notDisplayed = colourSwatchVariants.length - displayed;
      setTotals({ displayed, notDisplayed });
    }
  }, [colourSwatchVariants.length, showNumberOfSwatches]);

  if (colourSwatchVariants.length < 2) {
    return null;
  }

  const adTechId = product?.advertising?.associatedAdvertId;

  const handleClick = (colour: string) => {
    setSelectedColour(colour);
    if (adTechId) {
      dispatchAdTechAnalyticsEvent(product.aliases.pimProductId, adTechId);
    }
  };

  return (
    <div
      className={cx(styles.container, {
        ["container--withTotal"]: showNumberOfSwatches,
      })}
      data-component="ColourSwatches"
      ref={swatchListRef}
    >
      <div className={styles.list}>
        {colourSwatchVariants.map((variant: Variant) => {
          if (!("colourSwatch" in variant) || !variant.colourSwatch) {
            return null;
          }
          return (
            <button
              type="button"
              key={variant.id}
              title={variant.differentiators?.colour || ""}
              className={cx("button", {
                "button--unavailable": !variant.availability?.availableToOrder,
                "button--selected": variant.id === selectedVariant?.id,
              })}
              onClick={() => {
                handleClick(variant.differentiators?.colour || "");
              }}
            >
              <img
                src={`${variant.colourSwatch}${variant.colourSwatch.includes("?") ? "&" : "?"}wid=18&hei=18&fit=crop&sm=c`}
                className={styles.image}
                alt={`${variant.differentiators?.colour}${
                  variant.availability?.availableToOrder
                    ? ""
                    : " - Out of stock"
                }`}
              />
              <span className={styles.visuallyHidden}>
                {`${variant.differentiators?.colour} is ${
                  variant.availability?.availableToOrder
                    ? "available"
                    : "unavailable"
                }`}
              </span>
            </button>
          );
        })}
      </div>
      {showNumberOfSwatches && totals.notDisplayed > 0 && (
        <a
          data-testid="colour-swatch-more-available-total"
          className={cx("total")}
          href={selectedVariant?.pdpURL.url}
        >{`+ ${totals.notDisplayed}`}</a>
      )}
      {!showNumberOfSwatches && (
        <a className={styles.moreColours} href={selectedVariant?.pdpURL.url}>
          + More colours available
        </a>
      )}
    </div>
  );
}
